import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

// Modal for adding a video URL
const VideoUploadModal = ({ campaign, onClose, onSave }) => {
    const [videoUrl, setVideoUrl] = useState(campaign.appreciation_video_url || '');

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(campaign.id, videoUrl);
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <h2 className="text-xl font-bold">Add Appreciation Video</h2>
                    <p className="text-sm text-gray-500">For: {campaign.product_name}</p>
                    <input
                        type="url"
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                        placeholder="https://youtube.com/watch?v=..."
                        required
                        className="w-full p-2 border rounded-md"
                    />
                    <div className="flex justify-end gap-4">
                        <button type="button" onClick={onClose} className="py-2 px-4 bg-gray-200 font-semibold rounded-lg">Cancel</button>
                        <button type="submit" className="py-2 px-4 bg-orange-500 text-white font-semibold rounded-lg">Save Video URL</button>
                    </div>
                </form>
            </div>
        </div>
    );
};


export default function ManageAllCampaignsPage() {
    const { token } = useAuth();
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingCampaign, setEditingCampaign] = useState(null);

    const fetchCampaigns = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/campaigns/all`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Could not fetch campaigns.');
            const data = await response.json();
            setCampaigns(data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

    const handleUpdateDelivery = async (campaignId, newStatus) => {
        // This requires a new backend endpoint
        await fetch(`${process.env.REACT_APP_API_URL}/admin/campaigns/${campaignId}/delivery`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ deliveryStatus: newStatus })
        });
        fetchCampaigns(); // Refresh data
    };
    
    const handleSaveVideo = async (campaignId, videoUrl) => {
        await fetch(`${process.env.REACT_APP_API_URL}/admin/campaigns/${campaignId}/video`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ videoUrl })
        });
        setEditingCampaign(null);
        fetchCampaigns(); // Refresh data
    };


    if (loading) return <p>Loading campaigns...</p>;

    return (
        <div className="space-y-8">
            <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white">Manage All Campaigns</h1>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <table className="w-full text-left">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="p-2">Product</th>
                            <th className="p-2">Recipient</th>
                            <th className="p-2">Status</th>
                            <th className="p-2">Delivery</th>
                            <th className="p-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {campaigns.map(c => (
                            <tr key={c.id} className="border-b dark:border-gray-600">
                                <td className="p-2">{c.product_name}</td>
                                <td className="p-2">{c.first_name} {c.last_name}</td>
                                <td className="p-2 capitalize">{c.status}</td>
                                <td className="p-2 capitalize">{(c.delivery_status || 'N/A').replace('_', ' ')}</td>
                                <td className="p-2 space-x-2">
                                    {c.status === 'purchased' && c.delivery_status !== 'delivered' && (
                                        <button onClick={() => handleUpdateDelivery(c.id, 'delivered')} className="text-xs bg-green-500 text-white py-1 px-2 rounded">Mark Delivered</button>
                                    )}
                                    {c.delivery_status === 'delivered' && (
                                        <button onClick={() => setEditingCampaign(c)} className="text-xs bg-blue-500 text-white py-1 px-2 rounded">Add Video</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {editingCampaign && (
                <VideoUploadModal 
                    campaign={editingCampaign}
                    onClose={() => setEditingCampaign(null)}
                    onSave={handleSaveVideo}
                />
            )}
        </div>
    );
}